import type { Coordinate } from 'shared/@types';
import { parseApproximateLocation } from 'shared/library/approximate-location';
import useLocationStore, { type DeviceLocation } from 'shared/state/location';
import { fetchLocation } from './fetch-location';
import { fetchApproximateLocation } from './fetch-approximate-location';

const DEVICE_LOCATION_MAX_AGE = 5 * 60 * 1000;
let deviceRequest: { revision: number; promise: Promise<DeviceLocation> } | null = null;
let approximateRequest: { revision: number; promise: Promise<Coordinate | null> } | null = null;
let placeRevision = -1;
const placeRequests = new Map<string, Promise<string | null>>();

export function resolveUserLocation(
  deviceLocation: unknown,
  user: { approximate_location?: unknown; location?: { coordinates?: unknown[] } | null } | null,
  ipLocation: unknown,
) {
  const coordinates = user?.location?.coordinates;
  return (
    parseApproximateLocation(deviceLocation) ??
    parseApproximateLocation(user?.approximate_location) ??
    parseApproximateLocation({ latitude: coordinates?.[1], longitude: coordinates?.[0] }) ??
    parseApproximateLocation(ipLocation)
  );
}

export function requestCurrentLocation(fetchFn = fetchLocation): Promise<DeviceLocation> {
  const state = useLocationStore.getState();
  if (state.location && Date.now() - state.location.timestamp < DEVICE_LOCATION_MAX_AGE) {
    state.setErrorMessage(null);
    return Promise.resolve(state.location);
  }
  if (deviceRequest?.revision === state.revision) return deviceRequest.promise;

  const revision = state.revision;
  state.setErrorMessage(null);
  state.setLoading(true);
  const promise = Promise.resolve()
    .then(() => fetchFn())
    .then((position) => {
      if (useLocationStore.getState().revision !== revision) {
        throw new Error('Location request cancelled');
      }
      if (!parseApproximateLocation(position)) throw new Error('Invalid location');
      useLocationStore.getState().setLocation(position);
      return position;
    })
    .catch((error: unknown) => {
      if (useLocationStore.getState().revision === revision) {
        const message =
          typeof error === 'object' && error && 'message' in error
            ? String(error.message)
            : String(error);
        useLocationStore.getState().setErrorMessage(message);
      }
      throw error;
    })
    .finally(() => {
      if (useLocationStore.getState().revision === revision) {
        useLocationStore.getState().setLoading(false);
      }
      if (deviceRequest?.promise === promise) deviceRequest = null;
    });
  deviceRequest = { revision, promise };
  return promise;
}

export function requestApproximateLocation(
  fetchFn: () => Promise<Coordinate | null> = fetchApproximateLocation,
) {
  const state = useLocationStore.getState();
  if (state.approximateLocationReady) return Promise.resolve(state.approximateLocation);
  if (approximateRequest?.revision === state.revision) return approximateRequest.promise;

  const revision = state.revision;
  const promise = Promise.resolve()
    .then(() => fetchFn())
    .catch(() => null)
    .then((location) => {
      if (useLocationStore.getState().revision !== revision) return null;
      const coordinate = parseApproximateLocation(location);
      useLocationStore.getState().setApproximateLocation(coordinate);
      return coordinate;
    })
    .finally(() => {
      if (approximateRequest?.promise === promise) approximateRequest = null;
    });
  approximateRequest = { revision, promise };
  return promise;
}

export function requestLocationPlace(
  coordinate: Coordinate,
  languageCode: string,
  fetchFn = fetch,
) {
  const revision = useLocationStore.getState().revision;
  if (revision !== placeRevision) {
    placeRequests.clear();
    placeRevision = revision;
  }
  const key = `${languageCode}:${coordinate.latitude}:${coordinate.longitude}`;
  const publishPlace = (place: string | null) => {
    const state = useLocationStore.getState();
    if (state.revision !== revision) return null;
    if (
      place &&
      state.location?.latitude === coordinate.latitude &&
      state.location.longitude === coordinate.longitude
    )
      state.setPlace(place);
    return place;
  };
  const existing = placeRequests.get(key);
  if (existing) return existing.then(publishPlace);

  const promise = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      const params = new URLSearchParams({
        localityLanguage: languageCode,
        latitude: String(coordinate.latitude),
        longitude: String(coordinate.longitude),
      });
      const response = await fetchFn(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?${params}`,
        { signal: controller.signal },
      );
      if (!response.ok) return null;
      const data = await response.json();
      const place = [data.locality, data.principalSubdivision]
        .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
        .join(', ');
      return place || null;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  })().then(publishPlace);
  placeRequests.set(key, promise);
  void promise.then((place) => {
    if (!place && placeRequests.get(key) === promise) placeRequests.delete(key);
  });
  return promise;
}
